// Libraries.
import sdpTransform from 'sdp-transform'
import { cloneDeep, isArray, isFunction } from 'lodash/fp'

import { logManager } from '../logs'
const log = logManager.getLogger('SdpPipeline')

/**
 * Basic SDP pipeline runner.
 * Does not include any default handlers.
 * @method sdpPipeline
 * @param  {Array}      handlers       List of functions that transform the SDP.
 * @param  {String}     sdp            The session description.
 * @param  {RTCSdpType} info           Information about the session description.
 * @param  {RTCSdpType} info.type      The session description's type.
 * @param  {String}     info.endpoint  Which end of the connection created the SDP.
 * @param  {Boolean}    info.isInitiator Whether this session initiated the connection or not.
 * @return {String}     The modified session description.

 */
export function runPipeline (handlers, sdp, info) {
  const objectSdp = sdpTransform.parse(sdp)

  const originalSdp = Object.freeze(objectSdp)
  let newSdp = cloneDeep(originalSdp)

  if (isArray(handlers)) {
    handlers.forEach(handler => {
      if (isFunction(handler)) {
        newSdp = handler(newSdp, info, originalSdp)
      } else {
        log.error('SDP handler not a function; skipping.')
      }
    })
  }

  return sdpTransform.write(newSdp)
}

/**
 * Create an instance of the SDP pipeline.
 * Allows for persistent SDP handlers to be set and implicitly used.
 * @method createPipeline
 * @return {Object} An SDP pipeline.
 */
function createPipeline () {
  /**
   * SDP handlers that should be included with every pipeline run.
   * @type {Array}
   */
  let defaultHandlers = []

  /**
   * Use the pipeline to process an SDP.
   * @method run
   * @param  {Array}      handlers      List of functions that transform the SDP.
   * @param  {String}     sdp           The session description.
   * @param  {RTCSdpType} info          Information about the session description.
   * @param  {RTCSdpType} info.type     The session description's type.
   * @param  {String}     info.endpoint Which end of the connection created the SDP.
   * @return {String}     The modified session description.
   */
  function run (handlers = [], sdp, info) {
    return runPipeline(handlers.concat(defaultHandlers), sdp, info)
  }

  /**
   * Set the SDP handlers that should be included with every pipeline run.
   * @method setHandlers
   * @param  {Array} handlers List of SDP handler functions.
   */
  function setHandlers (handlers) {
    if (isArray(handlers)) {
      defaultHandlers = defaultHandlers.concat(handlers)
    }
  }

  /**
   * Get the SDP handlers that are included with every pipeline run.
   * @method getHandlers
   * @return {Array} List of SDP handler functions.
   */
  function getHandlers () {
    return defaultHandlers
  }

  return {
    run,
    setHandlers,
    getHandlers
  }
}

// Export an instance of the pipeline to be used by everything.
export default createPipeline()
